import { useMemo, useState } from "react";
import AdminHeader from "../components/admin/AdminHeader";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Loader2, Upload, FileText, CheckCircle2, AlertTriangle,
  CopyCheck, Trash2, Sparkles, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  Tabs, TabsList, TabsTrigger,
} from "../components/ui/tabs";
import { catalogBulkImport } from "../lib/api";
import { SLUGS, CATALOG_META } from "../components/admin/catalogMeta";

// Per-collection paste-templates that explain the bare-minimum required
// fields. Encoded as JSON strings so they round-trip cleanly into the
// textarea and immediately validate against the backend Pydantic models.
const TEMPLATES = {
  medications: `[
  {
    "id": "med-tretinoin-rx",
    "generic_name": "Tretinoin",
    "brand_names": ["Retin-A", "Renova"],
    "category": "RETINOID_TOPICAL",
    "description": "Topical retinoid prescribed for acne and photoaging."
  }
]`,
  ingredients: `[
  {
    "id": "ing-niacinamide",
    "name": "Niacinamide",
    "inci_name": "Niacinamide",
    "aliases": ["Vitamin B3", "Nicotinamide"],
    "category": "VITAMIN"
  }
]`,
  interactions: `[
  {
    "id": "int-tretinoin-aha",
    "medication_id": "med-tretinoin-rx",
    "ingredient_id": "ing-glycolic",
    "risk_level": "HIGH",
    "interaction_type": "USE_WITH_CAUTION",
    "what_happens_scientific": "Combined keratolytic effects amplify barrier disruption.",
    "what_happens_plain": "Stacking these can over-exfoliate and irritate.",
    "recommendation": "Use on alternate nights; rebuild barrier first.",
    "source_primary": "FDA label for tretinoin",
    "confidence_level": "FDA_DOCUMENTED",
    "last_reviewed": "2026-01-15"
  }
]`,
  products: `[
  {
    "id": "prod-cerave-mc",
    "name": "Moisturizing Cream",
    "brand": "CeraVe",
    "category": "MOISTURIZER",
    "key_ingredient_ids": ["ing-niacinamide"]
  }
]`,
};

function pluralize(n, w) {
  return `${n} ${w}${n === 1 ? "" : "s"}`;
}

function PreviewBucket({ title, color, count, rows, dataKey, emptyHint, accentIcon: Icon }) {
  if (count === 0) {
    return (
      <div className="rounded-xl border border-dashed border-stone-200 bg-white p-4 text-xs text-stone-400">
        <span className="font-semibold text-stone-500">{title}</span> — {emptyHint}
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-stone-200 bg-white overflow-hidden" data-testid={`preview-bucket-${dataKey}`}>
      <div
        className="flex items-center gap-2 border-b border-stone-200 bg-stone-50 px-4 py-2.5"
        style={{ borderLeft: `3px solid ${color}` }}
      >
        <Icon className="h-4 w-4" style={{ color }} />
        <span className="text-sm font-semibold text-stone-800">{title}</span>
        <span className="ml-auto text-xs font-bold tabular-nums" style={{ color }}>
          {count}
        </span>
      </div>
      <ul className="divide-y divide-stone-100 max-h-72 overflow-y-auto">
        {rows.map((r) => (
          <li key={`${dataKey}-${r.index}`} className="px-4 py-2.5 text-xs" data-testid={`preview-row-${dataKey}-${r.index}`}>
            <div className="flex items-baseline gap-2">
              <span className="font-mono text-[10px] text-stone-400">#{r.index}</span>
              <span className="font-medium text-stone-800 truncate">
                {r.id || r.preview?.id || "—"}
              </span>
            </div>
            {r.error && (
              <div className="mt-1 text-[11px] text-red-600 leading-snug">{r.error}</div>
            )}
            {r.preview && !r.error && (
              <div className="mt-1 truncate text-[11px] text-stone-500">
                {r.preview.generic_name || r.preview.name || r.preview.full_name || r.preview.recommendation || ""}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function AdminImport() {
  const [slug, setSlug] = useState("medications");
  const [text, setText] = useState("");
  const [skipDuplicates, setSkipDuplicates] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);  // dry-run preview result
  const [imported, setImported] = useState(null);  // actual import result

  const meta = CATALOG_META[slug];

  const summary = result?.summary;
  const canImport = !!result && summary?.valid > 0 && !importing;

  const fillTemplate = () => {
    setText(TEMPLATES[slug] || "");
    setResult(null);
    setImported(null);
  };

  const clear = () => {
    setText("");
    setResult(null);
    setImported(null);
  };

  const onPreview = async () => {
    if (!text.trim()) {
      toast.error("Paste some JSON first.");
      return;
    }
    setPreviewing(true);
    setImported(null);
    try {
      const res = await catalogBulkImport(slug, {
        payload: text,
        dryRun: true,
        skipDuplicates,
      });
      setResult(res);
      const s = res.summary;
      toast.success(
        `Parsed ${pluralize(s.total, "row")} — ${s.valid} valid, ${s.invalid} invalid, ${s.duplicates} duplicates`,
      );
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error(`Parse failed: ${msg}`);
      setResult(null);
    } finally {
      setPreviewing(false);
    }
  };

  const onImport = async () => {
    setImporting(true);
    try {
      const res = await catalogBulkImport(slug, {
        payload: text,
        dryRun: false,
        skipDuplicates,
      });
      setImported(res);
      const s = res.summary;
      toast.success(
        `Imported — created ${s.created}, updated ${s.updated}, skipped ${s.skipped}, failed ${s.failed}`,
      );
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error(`Import failed: ${msg}`);
    } finally {
      setImporting(false);
    }
  };

  const importedSummary = useMemo(() => imported?.summary, [imported]);

  return (
    <main
      data-testid="admin-import-page"
      className="min-h-screen bg-[#fdf8f3] px-4 sm:px-6 py-10 sm:py-12"
    >
      <div className="mx-auto max-w-5xl">
        <AdminHeader />

        <header className="mt-5 mb-8 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-stone-900">
              Bulk Import
            </h1>
            <p className="mt-1.5 text-sm text-stone-600 max-w-xl">
              Paste a JSON array (or NDJSON) of catalog rows and we'll parse, validate against the schema,
              dedupe, and import them in one batch. Foreign keys (medication_id, ingredient_id) are checked
              before insert.
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-white px-2.5 py-1 text-[10px] uppercase tracking-wider text-stone-600">
            <ShieldCheck className="h-3 w-3" /> Admin
          </span>
        </header>

        {/* Collection picker */}
        <Tabs value={slug} onValueChange={(v) => { setSlug(v); setResult(null); setImported(null); }}>
          <TabsList data-testid="import-slug-tabs">
            {SLUGS.map((s) => (
              <TabsTrigger key={s} value={s} data-testid={`import-tab-${s}`}>
                {CATALOG_META[s].label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Editor */}
        <div className="mt-5 rounded-2xl border border-stone-200 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 border-b border-stone-200 bg-stone-50 px-4 py-2.5">
            <FileText className="h-4 w-4 text-stone-500" />
            <span className="text-sm font-semibold text-stone-700">
              Paste {meta.label.toLowerCase()} JSON
            </span>
            <span className="ml-2 text-[11px] text-stone-500">
              JSON array, single object, or NDJSON
            </span>
            <div className="ml-auto flex items-center gap-2">
              <Button
                variant="ghost" size="sm"
                onClick={fillTemplate}
                data-testid="import-template-btn"
                className="text-xs"
              >
                <Sparkles className="mr-1.5 h-3 w-3" /> Use sample
              </Button>
              <Button
                variant="ghost" size="sm"
                onClick={clear}
                disabled={!text}
                data-testid="import-clear-btn"
                className="text-xs"
              >
                <Trash2 className="mr-1.5 h-3 w-3" /> Clear
              </Button>
            </div>
          </div>
          <textarea
            data-testid="import-textarea"
            value={text}
            onChange={(e) => { setText(e.target.value); setResult(null); setImported(null); }}
            spellCheck={false}
            placeholder={`Paste ${meta.label.toLowerCase()} as JSON…  e.g.\n[\n  {"id": "…", …}\n]`}
            className="block w-full min-h-[260px] resize-y bg-white p-4 text-[13px] font-mono text-stone-800 placeholder:text-stone-400 focus:outline-none"
          />
          <div className="flex items-center gap-3 border-t border-stone-200 bg-stone-50 px-4 py-3 flex-wrap">
            <label className="inline-flex items-center gap-2 text-xs text-stone-600 select-none cursor-pointer">
              <input
                type="checkbox"
                checked={skipDuplicates}
                onChange={(e) => setSkipDuplicates(e.target.checked)}
                data-testid="import-skip-duplicates"
                className="h-4 w-4 rounded border-stone-300"
              />
              Skip duplicates (don't overwrite existing IDs)
            </label>
            <div className="ml-auto flex items-center gap-2">
              <Button
                onClick={onPreview}
                disabled={previewing || !text.trim()}
                data-testid="import-preview-btn"
              >
                {previewing ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Parsing…</>
                ) : (
                  <>Parse & preview</>
                )}
              </Button>
              <Button
                onClick={onImport}
                disabled={!canImport}
                data-testid="import-confirm-btn"
                className="bg-stone-900 hover:bg-stone-800 text-white disabled:bg-stone-300"
              >
                {importing ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Importing…</>
                ) : (
                  <><Upload className="mr-2 h-4 w-4" /> Import {summary?.valid ?? 0} valid</>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Preview / result panel */}
        {result && !imported && (
          <section className="mt-8" data-testid="import-preview-panel">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-3">
              Preview
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-5">
              <SummaryTile label="Total"      value={summary.total}      tone="stone" />
              <SummaryTile label="Valid"      value={summary.valid}      tone="green" />
              <SummaryTile label="Duplicates" value={summary.duplicates} tone="amber" />
              <SummaryTile label="Invalid"    value={summary.invalid}    tone="red"   />
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              <PreviewBucket
                title="Valid rows" color="#16a34a"
                count={summary.valid} rows={result.valid}
                dataKey="valid" accentIcon={CheckCircle2}
                emptyHint="nothing parses cleanly yet."
              />
              <PreviewBucket
                title="Duplicates" color="#d97706"
                count={summary.duplicates} rows={result.duplicates}
                dataKey="duplicates" accentIcon={CopyCheck}
                emptyHint="no existing IDs collide."
              />
              <PreviewBucket
                title="Invalid rows" color="#dc2626"
                count={summary.invalid} rows={result.invalid}
                dataKey="invalid" accentIcon={AlertTriangle}
                emptyHint="all rows pass validation."
              />
            </div>

            {summary.duplicates > 0 && (
              <p className="mt-4 text-xs text-stone-500">
                Duplicates will {skipDuplicates ? <strong>be skipped</strong> : <strong>overwrite the existing rows</strong>} on import. Toggle the checkbox above to switch behavior.
              </p>
            )}
          </section>
        )}

        {imported && (
          <section className="mt-8" data-testid="import-result-panel">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-3">
              Import complete
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
              <SummaryTile label="Created"   value={importedSummary.created} tone="green" />
              <SummaryTile label="Updated"   value={importedSummary.updated} tone="blue"  />
              <SummaryTile label="Skipped"   value={importedSummary.skipped} tone="amber" />
              <SummaryTile label="Invalid"   value={importedSummary.invalid} tone="stone" />
              <SummaryTile label="Failed"    value={importedSummary.failed}  tone="red"   />
            </div>
            {imported.failed?.length > 0 && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-xs text-red-700 space-y-1.5"
                data-testid="import-failed-list">
                <div className="font-semibold uppercase tracking-wider text-[10px]">Failures</div>
                {imported.failed.map((f, i) => (
                  <div key={i}><code className="text-red-900">{f.id}</code>: {f.error}</div>
                ))}
              </div>
            )}
          </section>
        )}
      </div>
    </main>
  );
}

const TONE_BG = {
  green: "bg-green-50 border-green-200 text-green-900",
  amber: "bg-amber-50 border-amber-200 text-amber-900",
  red:   "bg-red-50 border-red-200 text-red-900",
  blue:  "bg-blue-50 border-blue-200 text-blue-900",
  stone: "bg-stone-50 border-stone-200 text-stone-900",
};

function SummaryTile({ label, value, tone = "stone" }) {
  return (
    <div
      className={`rounded-xl border p-3 text-center ${TONE_BG[tone]}`}
      data-testid={`summary-${label.toLowerCase()}`}
    >
      <div className="text-[10px] uppercase tracking-wider opacity-70 font-bold">{label}</div>
      <div className="text-2xl font-bold tabular-nums mt-0.5">{value ?? 0}</div>
    </div>
  );
}
